'use client';

import { useEffect } from 'react';

/**
 * How far in from a scrollable edge a press is still assumed to be the bar.
 *
 * Only overlay scrollbars need this. A classic bar reserves a gutter, so the
 * press lands outside the padding box and the measurement below identifies it
 * exactly. An overlay bar reserves nothing and is painted over the content, so
 * `clientWidth` covers the whole element and the press arrives at whatever is
 * underneath — geometrically identical to a real content press. A band along
 * the edge is the only way to tell, and erring toward "scrollbar" is the safe
 * direction: the cost is losing caret placement and focus-on-empty-area within
 * the band, against a field whose bars cannot be dragged at all.
 */
const OVERLAY_SCROLLBAR_SLOP_PX = 18;

/**
 * Narrowest gutter that counts as a real classic scrollbar rather than rounding.
 *
 * `getBoundingClientRect` is fractional but `clientWidth`/`clientHeight` are
 * rounded, so subtracting them leaves up to a pixel of noise in either
 * direction — a percentage-width editor with overlay bars measures a gutter of
 * roughly +0.3 rather than 0. Compared against zero that noise reads as a
 * reserved bar and turns the overlay handling off, which is the exact case this
 * file exists to catch.
 *
 * Nothing sits between the two: the noise is sub-pixel, while the narrowest bar
 * that actually reserves space is the 8px one `globals.css` defines, and
 * platform bars are wider still. Anything in that gap works; 2px keeps a clear
 * margin on both sides.
 */
const MIN_RESERVED_SCROLLBAR_PX = 2;

/** Whether the pointer is over the element's scrollbars rather than its content. */
function isOnScrollbar(element: HTMLElement, event: MouseEvent): boolean {
  const rect = element.getBoundingClientRect();
  const left = rect.left + element.clientLeft;
  const top = rect.top + element.clientTop;
  const right = left + element.clientWidth;
  const bottom = top + element.clientHeight;

  // Outside the padding box: the gutter a classic scrollbar reserves. Testing
  // both edges on each axis also covers RTL, where the vertical bar is on the
  // left, and the corner where two bars meet.
  if (event.clientX < left || event.clientX >= right || event.clientY < top || event.clientY >= bottom) {
    return true;
  }

  // Space between the padding box and the border box, i.e. the bar's gutter
  // plus any border. Too small to be a bar means nothing was reserved, so one
  // on this axis can only be an overlay. A border makes it read as reserved,
  // which just falls back to the exact test above — the conservative direction.
  const verticalGutter = rect.width - element.clientLeft - element.clientWidth;
  const horizontalGutter = rect.height - element.clientTop - element.clientHeight;

  const overlaysVertically =
    verticalGutter < MIN_RESERVED_SCROLLBAR_PX && element.scrollHeight > element.clientHeight;
  const overlaysHorizontally =
    horizontalGutter < MIN_RESERVED_SCROLLBAR_PX && element.scrollWidth > element.clientWidth;

  return (
    (overlaysVertically && right - event.clientX <= OVERLAY_SCROLLBAR_SLOP_PX) ||
    (overlaysHorizontally && bottom - event.clientY <= OVERLAY_SCROLLBAR_SLOP_PX)
  );
}

/**
 * Stops the markdown editor's scrollbars from being mistaken for its content.
 *
 * `MarkdownEditor` ships `contentAreaFocusPlugin`, which listens for mousedown
 * on the editable body and treats anything landing outside the contenteditable
 * as a click on the empty space below the text: it calls `preventDefault` and
 * puts the caret at the end of the document. That body is also the field's
 * scroll container, so pressing either scrollbar reaches the same listener —
 * the caret jumps to the end, Lexical scrolls it into view, and the field snaps
 * to the bottom instead of scrolling where the user aimed. The `preventDefault`
 * cancels the drag as well, so the thumb cannot be moved at all.
 *
 * The plugin binds to the body itself, so a capture-phase listener on the
 * document sees the press first and can stop it from ever getting there. Only
 * propagation is stopped, never the default action, so the browser still
 * handles the scrollbar normally.
 *
 * Remove this once theme-components' own plugin ignores its scrollbars.
 */
export function MarkdownEditorScrollbarGuard() {
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const body = target?.closest<HTMLElement>('.tp-md-editor .mdxeditor-root-contenteditable');
      if (!body) {
        return;
      }

      // Anything that is not a scrollbar — text, padding, the empty area below
      // the last block — has to pass through untouched. Capturing at the
      // document halts the event before any descendant sees it, so stopping a
      // content press would cut Lexical off from its own root element and
      // leave the field unclickable.
      //
      // This is deliberately the only test. The editable is stretched to fill
      // the body, so with overlay scrollbars a press on a bar lands on the
      // editable and is indistinguishable from content by target alone;
      // exempting the editable first would let those presses straight back
      // through to the plugin.
      if (!isOnScrollbar(body, event)) {
        return;
      }

      event.stopPropagation();
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, []);

  return null;
}
